import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { FormValidators } from '@corelabs/angular/forms';
import { CURRENCIES, CurrencyCode } from './currency';
import { CustomFormGroup, CustomPricing, RelatedEntityType, TierFormGroup, TierPricing } from './types';
import { tierArrayValidator } from './tier-array.validator';

const CENTS_IN_UNIT = 100;

function createPricesGroup(prices?: Partial<Record<CurrencyCode, number>>): FormGroup<Record<CurrencyCode, FormControl<number>>> {
    const controls = <Record<CurrencyCode, FormControl<number>>>{};
    for (const currency of CURRENCIES) {
        controls[currency] = new FormControl((prices?.[currency] ?? 0) / CENTS_IN_UNIT, {
            nonNullable: true,
            validators: [FormValidators.Required, FormValidators.MinValue(0)],
        });
    }
    return new FormGroup(controls);
}

export function createTierGroup(tier?: Partial<TierPricing>): TierFormGroup {
    return new FormGroup({
        label: new FormControl(tier?.label ?? '', { nonNullable: true, validators: [FormValidators.Required] }),
        minUsers: new FormControl(tier?.minUsers ?? 1, { nonNullable: true, validators: [FormValidators.Required] }),
        maxUsers: new FormControl<number | null>(tier?.maxUsers ?? null, { nonNullable: true }),
        prices: createPricesGroup(tier?.prices),
    });
}

export function createTierFormArray(tiers?: TierPricing[]): FormArray<TierFormGroup> {
    const controls = tiers?.map((tier) => createTierGroup(tier)) ?? [];
    return new FormArray<TierFormGroup>(controls, [tierArrayValidator]);
}

export function createCustomGroup(custom?: CustomPricing): CustomFormGroup {
    return new FormGroup({
        relatedEntityType: new FormControl<RelatedEntityType>(custom?.relatedEntityType ?? 'school', {
            nonNullable: true,
            validators: [FormValidators.Required],
        }),
        relatedEntityId: new FormControl(custom?.relatedEntityId ?? '', { nonNullable: true, validators: [FormValidators.Required] }),
        pricingList: createTierFormArray(custom?.pricingList),
    });
}

export function createCustomFormArray(items?: CustomPricing[]): FormArray<CustomFormGroup> {
    const controls = items?.map((custom) => createCustomGroup(custom)) ?? [];
    return new FormArray<CustomFormGroup>(controls);
}

export function tierGroupToValue(group: TierFormGroup): TierPricing {
    const raw = group.getRawValue();
    const prices = <Record<CurrencyCode, number>>{};
    for (const currency of CURRENCIES) {
        prices[currency] = Math.round(raw.prices[currency] * CENTS_IN_UNIT);
    }
    return {
        label: raw.label,
        minUsers: raw.minUsers,
        maxUsers: raw.maxUsers,
        prices,
    };
}

export function customGroupToValue(group: CustomFormGroup): CustomPricing {
    return {
        relatedEntityType: group.controls.relatedEntityType.value,
        relatedEntityId: group.controls.relatedEntityId.value,
        pricingList: group.controls.pricingList.controls.map((tier) => tierGroupToValue(tier)),
    };
}
